/**
 * SPA entrypoint. Providers, in order: TanStack Query (server state) → session
 * context → router (docs/08-frontend-and-ux.md §1.2).
 */

import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { I18nextProvider } from 'react-i18next';
import { App } from './app/App.js';
import { SessionProvider } from './app/session.js';
import i18n, { setupI18n } from './i18n/index.js';
import './ui/global.css';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // docs/08 §8: lists go stale after 30 s; the tree snapshot is keyed by
      // graph_version instead, so it never needs a timer.
      staleTime: 30_000,
      retry: false,
      refetchOnWindowFocus: false,
      networkMode: 'online',
    },
  },
});

const container = document.getElementById('root');
if (!container) throw new Error('#root element is missing from index.html');

// i18n resolves before the first paint so no screen ever flashes a raw key.
// (Not top-level await: the build targets browsers where that would force an
// ESM-only output for a bundle that has no other reason to require it.)
void setupI18n().then(() => {
  createRoot(container).render(
    <StrictMode>
      <I18nextProvider i18n={i18n}>
        <QueryClientProvider client={queryClient}>
          <BrowserRouter>
            <SessionProvider>
              <App />
            </SessionProvider>
          </BrowserRouter>
        </QueryClientProvider>
      </I18nextProvider>
    </StrictMode>,
  );
});
